import moment from 'moment';
import { useEffect, useMemo, useRef, useState } from 'react';
import { v4 as uuid } from 'uuid';
import { useModalsContext } from '../../../../../../providers/ModalsProvider/Modals.context.ts';
import { eChannelType } from '../../../../../../providers/SocketsProvider/Sockets.enums.ts';
import { eAlertType } from '../../../../../../providers/ToastProvider/Toast.enum.ts';
import { useToastContext } from '../../../../../../providers/ToastProvider/Toast.hook.ts';
import { useUserContext } from '../../../../../../providers/UserProvider/User.context.ts';
import { useWorkspaceContext } from '../../../../../../providers/WorkspaceProvider/Workspace.context.ts';
import { WhatsappApiService } from '../../../../../../pages/WhatsappApi/WhatsappApi.service.ts';
import { removeEmptyValuesFromObject } from '../../../../../../utils/removeEmptyValuesFromObject.ts';
import { ChatEnum, EventType } from '../../../../Chat.enum.ts';
import { updateEventAttachmentParser } from '../../../../Chat.functions.ts';
import { useChatContext } from '../../../../Chat.hook.ts';
import { ChatService } from '../../../../Chat.service.ts';
import { useWapiPlanSendGuard } from 'src/components/Chat/useWapiPlanSendGuard.hook';
import {
  tAttachment,
  tChatEvent,
  tUpdateEventAttachment,
  tUpdateEventData,
} from '../../../../Chat.types.ts';
import useMessageBubble from '../../../MessageContainer/components/EventBubble/components/MessageBubble/MessageBubble.hook.tsx';
import { useMessageBarContext } from '../../MessageBar.hook.ts';
import {
  tDeleteFollowUpOptions,
  tProgrammedMessageActionContext,
} from './ProgrammedMessageAction.types.ts';
import { useAnalytics } from 'src/hooks/analytics/useAnalytics.hook';
import { getModuleName } from '../../../../Chat.utils.ts';

// Drops pending-upload attachments (url is null/empty) before serializing a
// submit payload. Shipping one would round-trip through
// `updateEventAttachmentParser` as `{ url: '' }` and reintroduce the
// empty-payload class that messaging#813's CreateFollowUpBodyContent DTO
// rejects — same rule the UI enforces via `isEmpty`. Defense-in-depth layer 3.
function sanitizeAttachmentsForSubmit(
  attachments: tAttachment[] | undefined,
): tUpdateEventAttachment[] | undefined {
  return attachments
    ?.filter((attachment) => attachment?.url)
    .map((attachment) => updateEventAttachmentParser(attachment));
}

type tWapiScheduleLimit = Pick<
  tProgrammedMessageActionContext,
  'wapiScheduleLimit' | 'maxDate' | 'maxTime'
>;

const NO_WAPI_SCHEDULE_LIMIT: tWapiScheduleLimit = {
  wapiScheduleLimit: null,
  maxDate: null,
  maxTime: null,
};

// The latest a WhatsApp API free-text message can be scheduled: 5 minutes
// before its 24 h window closes. Only a window still open sets one.
// whatsapp-business-service keeps the last window's expiry after it closes,
// and sends Go's zero time ("0001-01-01T00:00:00Z") for a contact with no
// counter row; capping on either puts the date field's max before its min
// (today), so the picker offers no day at all.
function getWapiScheduleLimit(
  conversationExpiresAt: string | undefined,
  now: number,
): tWapiScheduleLimit {
  const expiresAtMs = Date.parse(conversationExpiresAt ?? '');
  if (!(expiresAtMs > now)) return NO_WAPI_SCHEDULE_LIMIT;

  const effectiveLimit = new Date(expiresAtMs - 5 * 60 * 1000);

  return {
    wapiScheduleLimit: moment(effectiveLimit).format(
      'DD/MM/YYYY [a las] HH:mm',
    ),
    maxDate: moment(effectiveLimit).format('YYYY-MM-DD'),
    maxTime: moment(effectiveLimit).format('HH:mm'),
  };
}

export default function useProgrammedMessageAction(): tProgrammedMessageActionContext {
  const { addAlert } = useToastContext();
  const { replaceMessageWithVariable } = useMessageBubble();
  const { selectedLeadForChatModal } = useModalsContext();
  const { trackEvent } = useAnalytics();

  const {
    selectedChat,
    setSelectedChat,
    chatEvents,
    setChatEvents,
    selectedFollowUp,
    followUps,
    setFollowUps,
  } = useChatContext();
  const { currentUserId, currentUser } = useUserContext();
  const { currentWorkspaceId } = useWorkspaceContext();
  const { blockWapiPlanSend } = useWapiPlanSendGuard();
  const {
    messageBarValue,
    setSelectedAction,
    setChatAttachments,
    setMessageBarValue,
    draftMessages,
    setDraftMessages,
  } = useMessageBarContext();

  const [loading, setLoading] = useState(false);
  const [triggerDate, setTriggerDate] = useState<string>();
  const [triggerTime, setTriggerTime] = useState<string>();
  const [followUpAttachments, setFollowUpAttachments] =
    useState<tAttachment[]>();

  // Synchronous guard against double-submit. `loading` state is for UI
  // (spinner); the ref is for correctness — it flips atomically and covers the
  // render gap between a click and React committing the disabled flag.
  const inFlightRef = useRef(false);
  const deleteInFlightRef = useRef(false);

  const localDate = moment().format('YYYY-MM-DD');
  const localTime = moment().format('HH:mm');

  useEffect(() => {
    setTriggerDate(undefined);
    setTriggerTime(undefined);
    setFollowUpAttachments(undefined);
  }, [selectedChat?.id]);

  const isDateInPast = useMemo<boolean>(() => {
    if (!triggerDate || !triggerTime) return false;
    return new Date(`${triggerDate}T${triggerTime}:00`) <= new Date();
  }, [triggerDate, triggerTime]);

  // Empty-payload guard. Canonical rule lives in messaging#813
  // (CreateFollowUpBodyContent DTO + FollowUpTrigger); frontend is
  // defense-in-depth. `.every(a => a?.url)` rather than `.some`: if any
  // attachment is still uploading, the whole submit ships at least one
  // `{ url: '' }` row (see updateEventAttachmentParser) — which is exactly the
  // empty payload class we're trying to block.
  const hasText = Boolean(messageBarValue?.trim());
  const hasReadyAttachments = Boolean(
    followUpAttachments?.length &&
      followUpAttachments.every((attachment) => attachment?.url),
  );
  const isEmpty = !hasText && !hasReadyAttachments;

  const isCreatingNewFollowUp = selectedFollowUp?.id === '';
  const isWapiChat =
    selectedLeadForChatModal?.chat?.integration === eChannelType.WHATSAPP_API;
  // Computed per render rather than memoized on the expiry, so a window that
  // closes while the panel is open stops capping at the next render.
  const { wapiScheduleLimit, maxDate, maxTime } =
    isCreatingNewFollowUp && isWapiChat
      ? getWapiScheduleLimit(
          selectedChat?.sentTemplateCounter?.conversationExpiresAt,
          Date.now(),
        )
      : NO_WAPI_SCHEDULE_LIMIT;

  useEffect(() => {
    if (selectedFollowUp) {
      setTriggerDate(
        moment(selectedFollowUp.trigger_date).format('YYYY-MM-DD'),
      );
      setTriggerTime(moment(selectedFollowUp.trigger_date).format('HH:mm'));
      setFollowUpAttachments(selectedFollowUp.content.attachments);
      setMessageBarValue(selectedFollowUp.content.text);
    }
  }, [selectedFollowUp?.id]);

  async function handleTriggerDateTimeChange() {
    if (inFlightRef.current) return;
    if (triggerDate && triggerTime && new Date(`${triggerDate}T${triggerTime}:00`) <= new Date()) return;
    if (isEmpty) return;

    inFlightRef.current = true;
    try {
      const nextDraftMessages = draftMessages ? [...draftMessages] : [];
      let currentDraft = -1;

      if (selectedChat?.leadId && Array.isArray(draftMessages)) {
        currentDraft = draftMessages.findIndex(
          (draft) => draft.leadId === selectedChat.leadId,
        );
      }
      const dateObj = new Date(`${triggerDate}T${triggerTime}:00`);

      if (selectedFollowUp?.id) {
        const text = replaceMessageWithVariable(messageBarValue);
        await updateEvent(selectedFollowUp.id, {
          type: EventType.FOLLOW_UP,
          follow_up: {
            trigger_date: dateObj,
          },
          content: {
            text,
            attachments: sanitizeAttachmentsForSubmit(followUpAttachments),
          },
        });

        setChatEvents((prevMessages) => {
          if (!prevMessages) return prevMessages;

          const newMessages = {
            ...prevMessages,
            events: prevMessages.map((event) => {
              if (event.id === selectedFollowUp.id) {
                return {
                  ...event,
                  content: {
                    ...event.content,
                    text,
                    attachments: followUpAttachments,
                  },
                  followUp: {
                    ...event.followUp,
                    triggerDate: `${triggerDate}T${triggerTime}:00`,
                    consumed: event.followUp?.consumed || false,
                  },
                };
              }

              return event;
            }),
          };

          return newMessages.events;
        });
      } else {
        const wasSent = await createNewFollowUp(
          dateObj.toISOString(),
          followUpAttachments,
        );
        // RET-2349: a send the plan refused keeps the panel and the draft.
        if (!wasSent) return;
      }
      if (currentDraft > -1) {
        nextDraftMessages.splice(currentDraft, 1);
        setDraftMessages(nextDraftMessages);
      }

      trackEvent('front.chat.scheduled_message.create.save_btn.click', {
        displayName: 'Crear y guardar mensaje programado',
        description: 'Cuántas usuarias crean un mensaje programado',
        ctaType: getModuleName(),
      });
      setSelectedAction(ChatEnum.CHAT);
    } finally {
      inFlightRef.current = false;
    }
  }

  /**
   * `false` only when the plan guard refused the send (RET-2349); every other
   * outcome, a failed request included, keeps answering `true` as before.
   */
  async function createNewFollowUp(
    triggerDate: string,
    attachments?: tAttachment[],
  ): Promise<boolean> {
    // RET-2349 (KTD4): guard the same channel the request carries.
    const channelType =
      selectedLeadForChatModal?.chat?.integration || eChannelType.WHATSAPP_WEB;
    if (blockWapiPlanSend(channelType)) return false;

    try {
      if (
        !selectedChat ||
        !currentUserId ||
        !chatEvents ||
        !selectedLeadForChatModal
      )
        return true;

      setLoading(true);

      let followUpId = uuid();
      const createFollowUpRequest = {
        chatId: selectedChat?.id,
        data: {
          id: followUpId,
          channel_type: channelType,
          content: {
            text: replaceMessageWithVariable(messageBarValue) || '',
            attachments: sanitizeAttachmentsForSubmit(attachments) || [],
          },
          trigger_date: triggerDate,
          created_by: currentUserId,
        },
      };

      await ChatService.createFollowUp(createFollowUpRequest).then(
        (response) => {
          followUpId = response.id;
        },
      );

      const newChatEvent: tChatEvent = {
        id: followUpId,
        agendaEvent: null,
        chatID: selectedChat.id,
        type: EventType.FOLLOW_UP,
        content: {
          text: replaceMessageWithVariable(messageBarValue) || '',
          attachments: attachments || [],
          linkPreview: null,
          quotedEventID: null,
        },
        followUp: {
          triggerDate,
          consumed: false,
        },
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        deletedAt: '',
        message: null,
        note: null,
        createdBy: currentUserId,
      };

      setFollowUps([newChatEvent, ...followUps]);

      setChatEvents([...chatEvents, newChatEvent]);

      setMessageBarValue('');
      setChatAttachments([]);
      setLoading(false);
    } catch (error) {
      const err = error as { status?: number };

      if (err.status === 412) {
        addAlert({
          type: eAlertType.ERROR,
          message: `La fecha del mensaje programado debe ser como mínimo 3 mínutos posteriores a la hora actual.`,
        });
      } else {
        addAlert({
          type: eAlertType.ERROR,
          message: `Error al programar el mensaje: ${error}`,
        });
      }
    } finally {
      setLoading(false);
    }
    return true;
  }

  const CANCEL_FAILED_MESSAGE =
    'No se pudo cancelar el envío programado. La plantilla no fue eliminada, inténtalo de nuevo.';

  async function deleteFollowUp(
    eventId: string,
    options?: tDeleteFollowUpOptions,
  ) {
    // INT-1500 U12: the "Próximos envíos" bar cancels cards this tab may not
    // hold (history the thread has not loaded, a card scheduled from another
    // tab), so it passes what the server told it about the card.
    const knownFollowUp = options?.knownFollowUp;
    // Same synchronous double-submit guard as handleTriggerDateTimeChange: a
    // double-click would run cancel+delete twice and surface a spurious error
    // toast for the second delete.
    if (deleteInFlightRef.current) return;
    deleteInFlightRef.current = true;
    try {
      if (
        !currentWorkspaceId ||
        !currentUserId ||
        (!followUps?.length && !knownFollowUp) ||
        !chatEvents
      ) {
        return;
      }

      setLoading(true);

      // BUG-687: a WAPI scheduled template is sent by a WBS campaign, not by
      // the follow-up cron. Deleting only the card leaves the campaign
      // scheduled and the template sends anyway — cancel the campaign first,
      // and only remove the card once the send is truly stopped.
      const event =
        followUps?.find((followUp) => followUp.id === eventId) ??
        chatEvents.find((chatEvent) => chatEvent.id === eventId);

      let uncancellableWapiTemplate = false;

      if (event?.isWapiTemplate || knownFollowUp?.isWapiTemplate) {
        const campaignId =
          event?.followUp?.campaignId ?? knownFollowUp?.campaignId;

        if (campaignId) {
          // WBS requires a valid workspace-user UUID in cancelled_by; without
          // it the cancel would 400. Fail fast and keep the card so the user
          // can retry once the user provider hydrates.
          if (!currentUser?.uuid) {
            addAlert({ type: eAlertType.ERROR, message: CANCEL_FAILED_MESSAGE });
            return;
          }

          try {
            await WhatsappApiService.cancelCampaign(
              currentWorkspaceId,
              campaignId,
              currentUser.uuid,
            );
          } catch (cancelError) {
            const status = (cancelError as { status?: number })?.status;
            // 422 = the campaign is no longer scheduled (already sent or
            // already cancelled); 404 = campaign gone. Nothing left to stop in
            // either case, so removing the card is safe. Any other failure
            // means the send may still fire — keep the card visible.
            if (status !== 422 && status !== 404) {
              addAlert({ type: eAlertType.ERROR, message: CANCEL_FAILED_MESSAGE });
              return;
            }
          }
        } else {
          uncancellableWapiTemplate = true;
        }
      }

      await ChatService.deleteEvent(
        {
          workspaceId: currentWorkspaceId,
          eventId: eventId,
        },
        currentUserId,
      );

      // Functional setters: two awaits (cancel + delete) sit between the
      // render-time captures and this commit — a value-style set would clobber
      // any socket-appended events from that window (see CLAUDE.md "Async
      // Guards and WebSocket Race Conditions").
      setFollowUps((prev) => prev.filter((followUp) => followUp.id !== eventId));
      setChatEvents((prev) => prev?.filter((event) => event.id !== eventId));

      if (uncancellableWapiTemplate) {
        // Card without a campaign link (scheduled before BUG-687 or during the
        // rollout window): there is nothing to cancel automatically, so be
        // honest that the send may still fire.
        addAlert({
          type: eAlertType.WARNING,
          message:
            'La tarjeta se eliminó, pero no encontramos el envío vinculado y podría enviarse de todas formas. Contacta a soporte para cancelarlo.',
        });
      }
    } catch (error) {
      addAlert({
        type: eAlertType.ERROR,
        message: `Error al eliminar followUp ${error}`,
      });
    } finally {
      deleteInFlightRef.current = false;
      setLoading(false);
    }
  }

  async function updateEvent(eventId: string, data: tUpdateEventData) {
    try {
      if (!currentWorkspaceId || !currentUserId || !chatEvents) {
        return;
      }

      await ChatService.updateEvent(
        {
          eventId: eventId,
          workspaceId: currentWorkspaceId,
          data: {
            ...data,
            content: {
              ...data.content,
              text: replaceMessageWithVariable(data?.content?.text || ''),
            },
          },
        },
        currentUserId,
      );

      if (!selectedChat || !followUps?.length) {
        return;
      }

      const triggerDate = `${moment(data.follow_up?.trigger_date).format('YYYY-MM-DD')}T${moment(data.follow_up?.trigger_date).format('HH:mm')}:00`;

      const updatedFollowups: tChatEvent[] = followUps.map((followup) => {
        if (followup.id === eventId) {
          return {
            ...followup,
            // Spread first: dropping fields here severs the campaignId link and
            // an edit-then-delete would skip the campaign cancel (BUG-687).
            followUp: {
              ...followup.followUp,
              consumed: !!followup.followUp?.consumed,
              triggerDate,
            },
            content: {
              ...followup.content,
              text: replaceMessageWithVariable(data?.content?.text || ''),
              attachments:
                data.content?.attachments?.map(
                  (attachment) =>
                    removeEmptyValuesFromObject(attachment) as tAttachment,
                ) || [],
              linkPreview: followup.content?.linkPreview || null,
              quotedEventID: followup.content?.quotedEventID || null,
            },
          };
        }

        return followup;
      });

      const updatedEvents: tChatEvent[] = chatEvents.map((event) => {
        if (event.id === eventId) {
          return {
            ...event,
            // Spread first: keep campaignId (and future fields) — see above.
            followUp: {
              ...event.followUp,
              consumed: !!event.followUp?.consumed,
              triggerDate,
            },
            content: {
              ...event.content,
              text: replaceMessageWithVariable(data?.content?.text || ''),
              attachments:
                data.content?.attachments?.map(
                  (file) =>
                    ({
                      vcard: null,
                      decryption: null,
                      duration: null,
                      filename: file.filename,
                      latitude: null,
                      longitude: null,
                      mediaType: file.media_type,
                      mimeType: file.mimetype,
                      url: file.url,
                    }) as tAttachment,
                ) || [],
              linkPreview: event.content?.linkPreview || null,
              quotedEventID: event.content?.quotedEventID || null,
            },
          };
        }
        return event;
      });

      setFollowUps(updatedFollowups);
      setSelectedChat({
        ...selectedChat,
        chatEvents: {
          ...selectedChat.chatEvents,
          events: updatedEvents,
        },
      });

      setChatEvents(updatedEvents);

      setSelectedAction(ChatEnum.CHAT);
      setMessageBarValue('');
      setChatAttachments([]);
    } catch (error) {
      addAlert({
        type: eAlertType.ERROR,
        message: `No pudimos actualizar la nota ${error}`,
      });
    }
  }

  return {
    createNewFollowUp,
    deleteFollowUp,
    updateEvent,
    loading,
    setLoading,
    triggerDate,
    setTriggerDate,
    triggerTime,
    setTriggerTime,
    followUpAttachments,
    setFollowUpAttachments,
    handleTriggerDateTimeChange,
    localDate,
    localTime,
    isDateInPast,
    isEmpty,
    wapiScheduleLimit,
    maxDate,
    maxTime,
  };
}
